import { UnorderedListOutlined } from '@ant-design/icons';
import { Button, Spin, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Link, match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import IInvestment from '~Api/Investment/IInvestment';
import IInvestor from '~Api/Investor/IInvestor';
import IInvestorAccountInvestment from '~Api/Investor/IInvestorAccountInvestment';
import {
    investmentGetAction,
    investmentInvestorsListAction,
} from '~Investments/actions';
import {
    investmentInvestorAccountInvestmentsSelector,
    investmentSelector,
} from '~Investments/selectors';
import InvestmentTransactionsModal from '~Investors/Investor/InvestmentTransactionsModal';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';

interface IState {
    modalIsOpen: boolean;
    modalUuid: string;
}

interface IMatch {
    investmentUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    investment: IInvestment;
    investorAccountInvestments: IDictionary<IInvestorAccountInvestment>;
}

interface IPropsDispatch {
    investmentGet: () => void;
    investorAccountInvestmentsList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Investors extends React.Component<Props, IState> {
    public state: IState = {
        modalIsOpen: false,
        modalUuid: null,
    };

    constructor(props: Props) {
        super(props);

        this.onClickTransactions = this.onClickTransactions.bind(this);
        this.onCloseModal = this.onCloseModal.bind(this);
        this.summary = this.summary.bind(this);
    }

    public componentDidMount(): void {
        const { investment, investorAccountInvestments } = this.props;

        if (!investment) {
            this.props.investmentGet();
        }

        if (!investorAccountInvestments) {
            this.props.investorAccountInvestmentsList();
        }
    }

    public render(): JSX.Element {
        const { investment, investorAccountInvestments, match } = this.props;
        const { modalIsOpen, modalUuid } = this.state;

        if (!investment || !investorAccountInvestments) {
            return (
                <Layout uuid={match.params.investmentUuid} section='investors'>
                    <Typography.Title level={2}>Investors</Typography.Title>
                    <Spin/>
                </Layout>
            );
        }

        const currencyFormatter = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const columns: ColumnType<IInvestorAccountInvestment>[] = [
            {
                dataIndex: 'investor',
                defaultSortOrder: 'ascend',
                render: (investor: IInvestor) => investor && <Link to={`/investors/${investor.uuid}`}>{investor.name}</Link>,
                sorter: (a: IInvestorAccountInvestment, b: IInvestorAccountInvestment) => {
                    return a.investor.name.localeCompare(b.investor.name);
                },
                title: 'Investor',
            },
            {
                dataIndex: 'principalBalance',
                render: (principalBalance: number) => currencyFormatter.format(principalBalance),
                sorter: (a: IInvestorAccountInvestment, b: IInvestorAccountInvestment) => {
                    return a.principalBalance > b.principalBalance ? 1 : -1;
                },
                title: 'Principal',
                width: '15%',
            },
            {
                dataIndex: 'interestBalance',
                render: (interestBalance: number) => currencyFormatter.format(interestBalance),
                sorter: (a: IInvestorAccountInvestment, b: IInvestorAccountInvestment) => {
                    return a.interestBalance > b.interestBalance ? 1 : -1;
                },
                title: 'Interest',
                width: '15%',
            },
            {
                dataIndex: 'uuid',
                render: (uuid: string) => {
                    const onClick: () => void = () => this.onClickTransactions(uuid);

                    const modalBlock: JSX.Element = modalUuid === uuid && (
                        <InvestmentTransactionsModal
                            investorAccountInvestmentUuid={uuid}
                            isOpen={modalIsOpen}
                            onCancel={this.onCloseModal}
                        />
                    );

                    return (
                        <>
                            <Button icon={<UnorderedListOutlined/>} onClick={onClick} />
                            {modalBlock}
                        </>
                    );
                },
                width: '2%',
            },
        ];

        return (
            <Layout uuid={match.params.investmentUuid} section='investors'>
                <Typography.Title level={2}>Investors</Typography.Title>
                <Table
                    columns={columns}
                    dataSource={_.values(investorAccountInvestments)}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                    summary={this.summary}
                />
            </Layout>
        );
    }

    private summary(): JSX.Element {
        const { investorAccountInvestments } = this.props;

        let investmentCount: number = 0;
        let principalTotal: number = 0;
        let interestTotal: number = 0;

        _.forEach(investorAccountInvestments, (investorAccountInvestment: IInvestorAccountInvestment) => {
            investmentCount++;
            principalTotal += investorAccountInvestment.principalBalance;
            interestTotal += investorAccountInvestment.interestBalance;
        });

        const currencyFormatter = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>{investmentCount}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={1}><strong>{currencyFormatter.format(principalTotal)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2}><strong>{currencyFormatter.format(interestTotal)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3}/>
            </Table.Summary.Row>
        );
    }

    private onClickTransactions(investorAccountInvestmentUuid: string): void {
        this.setState({
            modalIsOpen: true,
            modalUuid: investorAccountInvestmentUuid,
        });
    }

    private onCloseModal(): void {
        this.setState({
            modalIsOpen: false,
        });
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        investment: investmentSelector(state, ownProps.match.params.investmentUuid),
        investorAccountInvestments: investmentInvestorAccountInvestmentsSelector(state, ownProps.match.params.investmentUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        investmentGet: () => dispatch(investmentGetAction(ownProps.match.params.investmentUuid)),
        investorAccountInvestmentsList: () => dispatch(investmentInvestorsListAction(ownProps.match.params.investmentUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Investors);
